import axios from 'axios';
import { api } from './api.ts';
import { getApiBaseUrl } from '../utils/getApiUrl.ts';

const BASE = '/api/v1/import';

// ── Types ──────────────────────────────────────────────────────────────────

export type ImportActionType = 'rendimento_fii' | 'receita' | 'despesa' | 'ignorar';
export type ImportSessionStatus = 'parsed' | 'applied';
export type ImportEntryStatus = 'matched' | 'pending' | 'ignored' | 'applied' | 'error';

export interface ImportMappingRule {
  id: number;
  user_id: number;
  name: string;
  pattern: string;
  action_type: ImportActionType;
  account_id: number | null;
  category_id: number | null;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface CreateRuleRequest {
  name: string;
  pattern: string;
  action_type: ImportActionType;
  account_id?: number | null;
  category_id?: number | null;
  priority?: number;
}

export interface PreviewResult {
  description: string;
  matched_rule_id: number | null;
  matched_rule_name: string;
  captured_values: string[];
  action_type: ImportActionType | null;
}

export interface ImportSession {
  id: number;
  user_id: number;
  file_name: string;
  file_type: string;
  status: ImportSessionStatus;
  total_rows: number;
  matched_rows: number;
  pending_rows: number;
  ignored_rows: number;
  applied_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ImportEntry {
  id: number;
  import_session_id: number;
  user_id: number;
  raw_description: string;
  date: string;
  amount: number;
  is_credit: boolean;
  status: ImportEntryStatus;
  matched_rule_id: number | null;
  captured_ticker: string;
  fii_shares: number;
  action_type: ImportActionType | null;
  account_id: number | null;
  category_id: number | null;
  fii_asset_id: number | null;
  fii_income_type: 'dividendo' | 'jcp' | null;
  created_transaction_id: number | null;
  created_fii_income_id: number | null;
  error_message: string;
  row_index: number;
}

export interface UpdateEntryRequest {
  status: ImportEntryStatus;
  action_type?: ImportActionType | null;
  account_id?: number | null;
  category_id?: number | null;
  fii_asset_id?: number | null;
  fii_income_type?: 'dividendo' | 'jcp' | null;
}

export interface ApplyResult {
  applied: number;
  ignored: number;
  skipped: number;
  errors: Array<{ entry_id: number; description: string; error: string }>;
}

export interface BackfillResult {
  created: number;
  skipped: number;
  errors: Array<{ entry_id: number; description: string; error: string }>;
}

// ── Rules API ──────────────────────────────────────────────────────────────

export const getRules = () =>
  api.get<ImportMappingRule[]>(`${BASE}/rules`).then(r => r.data);

export const createRule = (data: CreateRuleRequest) =>
  api.post<ImportMappingRule>(`${BASE}/rules`, data).then(r => r.data);

export const updateRule = (id: number, data: CreateRuleRequest) =>
  api.put<ImportMappingRule>(`${BASE}/rules/${id}`, data).then(r => r.data);

export const deleteRule = (id: number) =>
  api.delete(`${BASE}/rules/${id}`).then(r => r.data);

export const reorderRules = (orderedIds: number[]) =>
  api.put(`${BASE}/rules/reorder`, { ordered_ids: orderedIds }).then(r => r.data);

export const previewRules = (descriptions: string[]) =>
  api.post<PreviewResult[]>(`${BASE}/rules/preview`, { descriptions }).then(r => r.data);

// ── Sessions API ───────────────────────────────────────────────────────────

export const getSessions = () =>
  api.get<ImportSession[]>(`${BASE}/sessions`).then(r => r.data);

export const createSession = (formData: FormData) => {
  const token = localStorage.getItem('token');
  return axios.post<{ session: ImportSession; entries: ImportEntry[] }>(
    `${getApiBaseUrl()}${BASE}/sessions`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  ).then(r => r.data);
};

export const getSession = (id: number) =>
  api.get<ImportSession>(`${BASE}/sessions/${id}`).then(r => r.data);

export const getSessionEntries = (sessionId: number) =>
  api.get<ImportEntry[]>(`${BASE}/sessions/${sessionId}/entries`).then(r => r.data);

export const updateEntry = (sessionId: number, entryId: number, data: UpdateEntryRequest) =>
  api.put<ImportEntry>(`${BASE}/sessions/${sessionId}/entries/${entryId}`, data).then(r => r.data);

export const applySession = (sessionId: number) =>
  api.post<ApplyResult>(`${BASE}/sessions/${sessionId}/apply`).then(r => r.data);

export const deleteSession = (id: number) =>
  api.delete(`${BASE}/sessions/${id}`).then(r => r.data);

export const backfillFIITransactions = () =>
  api.post<BackfillResult>(`${BASE}/backfill-fii-transactions`).then(r => r.data);

export const ACTION_LABELS: Record<ImportActionType, string> = {
  rendimento_fii: 'Rendimento FII',
  receita: 'Receita',
  despesa: 'Despesa',
  ignorar: 'Ignorar',
};

export const ACTION_COLORS: Record<ImportActionType, string> = {
  rendimento_fii: '#10b981',
  receita: '#3b82f6',
  despesa: '#ef4444',
  ignorar: '#9ca3af',
};
